import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { PasswordGate, ProtectedRoute, RequireRole } from "@/shared/auth/ProtectedRoute";
import { AppShell } from "@/widgets/AppShell";
import { Spinner } from "@/shared/ui/Spinner";
import { ChunkErrorBoundary, RouteError } from "./RouteError";

const LoginPage     = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const ControlCenterPage = lazy(() => import("@/pages/ControlCenterPage").then((m) => ({ default: m.ControlCenterPage })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const KpiPage = lazy(() =>
  import("@/pages/KpiPage").then((m) => ({ default: m.KpiPage })),
);
const SitesPage      = lazy(() => import("@/pages/SitesPage").then((m) => ({ default: m.SitesPage })));
const SiteDetailPage = lazy(() => import("@/pages/SiteDetailPage").then((m) => ({ default: m.SiteDetailPage })));
const SensorsPage   = lazy(() => import("@/pages/SensorsPage").then((m) => ({ default: m.SensorsPage })));
const AlertsPage    = lazy(() => import("@/pages/AlertsPage").then((m) => ({ default: m.AlertsPage })));
const AnomalyPage   = lazy(() => import("@/pages/AnomalyPage").then((m) => ({ default: m.AnomalyPage })));
const SettingsPage    = lazy(() => import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ProfilePage     = lazy(() => import("@/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const DigitalTwinPage = lazy(() => import("@/pages/DigitalTwinPage").then((m) => ({ default: m.DigitalTwinPage })));
const RiskPage      = lazy(() => import("@/pages/RiskPage").then((m) => ({ default: m.RiskPage })));
const ScenariosPage = lazy(() => import("@/pages/ScenariosPage").then((m) => ({ default: m.ScenariosPage })));
const ReportsPage   = lazy(() => import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const BlueprintsPage = lazy(() => import("@/pages/BlueprintsPage").then((m) => ({ default: m.BlueprintsPage })));
const DevicesPage    = lazy(() => import("@/pages/DevicesPage").then((m) => ({ default: m.DevicesPage })));
const EvacuationPage = lazy(() => import("@/pages/EvacuationPage").then((m) => ({ default: m.EvacuationPage })));
const GasDbPage     = lazy(() => import("@/pages/GasDbPage").then((m) => ({ default: m.GasDbPage })));
const CapturesPage  = lazy(() => import("@/pages/CapturesPage").then((m) => ({ default: m.CapturesPage })));
const CamerasPage   = lazy(() => import("@/pages/CamerasPage").then((m) => ({ default: m.CamerasPage })));
const UsersPage       = lazy(() => import("@/pages/admin/UsersPage").then((m) => ({ default: m.UsersPage })));
const OrgsPage        = lazy(() => import("@/pages/admin/OrgsPage").then((m) => ({ default: m.OrgsPage })));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePasswordPage").then((m) => ({ default: m.ChangePasswordPage })));

/**
 * Every lazy page goes through here, so this is also where a **stale bundle**
 * is caught: after a deploy the hashed chunk an open tab asks for is gone, the
 * dynamic import rejects, and without a boundary React Router's default
 * developer screen replaced the entire console. `ChunkErrorBoundary` keeps the
 * shell mounted and reloads the tab once onto the new build.
 */
function Suspended({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense
        fallback={
          <div className="grid place-items-center h-[60dvh] text-[var(--fg-subtle)]">
            <Spinner size={20} />
          </div>
        }
      >
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <LoginPage />
      </Suspended>
    ),
  },
  {
    element: <ProtectedRoute />,
    // Catch-all: anything the per-page ChunkErrorBoundary doesn't own (loaders,
    // the shell itself) lands here instead of React Router's default screen.
    errorElement: <RouteError />,
    children: [
      // Forced password change lives OUTSIDE the app shell + password gate so a
      // must-change user can actually reach it (the gate redirects here).
      { path: "change-password", element: <Suspended><ChangePasswordPage /></Suspended> },
      {
        element: <PasswordGate />,
        children: [
          {
            element: <AppShell />,
            children: [
              // ── 전 역할(worker 포함) — 작업자 4화면 + 계정 ────────────
              // 라우트 게이트는 navItems 의 minRole 과 짝으로 움직인다
              // (18 화면개편 §3-1: 메뉴에서 숨겨도 URL 직접 입력이면 열리던
              // 구멍을 여기서 막는다). 서버가 진짜 규칙이고 이건 UX 방어선.
              { index: true,         element: <Suspended><ControlCenterPage /></Suspended> },
              { path: "sensors",     element: <Suspended><SensorsPage /></Suspended> },
              { path: "alerts",      element: <Suspended><AlertsPage /></Suspended> },
              { path: "evacuation",  element: <Suspended><EvacuationPage /></Suspended> },
              // 관제 화면(NOC)은 통합 관제로 통합됨 — 기존 딥링크·`?kiosk=1`은
              // 같은 화면으로 유지한다.
              { path: "noc",         element: <Suspended><ControlCenterPage /></Suspended> },
              // 내 계정 = 나에 관한 것(정보·알림·보안), 설정 = 시스템·조직에
              // 관한 것. 계정 메뉴의 두 항목이 서로 다른 곳에 도착한다.
              { path: "profile",     element: <Suspended><ProfilePage /></Suspended> },

              // ── 운영원(operator) 이상 ─────────────────────────────────
              {
                element: <RequireRole min="operator" />,
                children: [
                  { path: "sites",       element: <Suspended><SitesPage /></Suspended> },
                  { path: "sites/:id",   element: <Suspended><SiteDetailPage /></Suspended> },
                  { path: "risk",        element: <Suspended><RiskPage /></Suspended> },
                  // 이상탐지 = 조기 징후 + 센서 건전성(flatline·drift) — 운영
                  // 도구라 operator 유지 (18 v2.1, 사용자 결정 2026-08-27).
                  { path: "anomaly",     element: <Suspended><AnomalyPage /></Suspended> },
                  // CCTV = 품목5 소관이라 과제 스코프 밖이지만 현장 시각확인
                  // 용도로 유지. 과제 시연 경로에서는 제외.
                  { path: "cameras",     element: <Suspended><CamerasPage /></Suspended> },
                  { path: "settings",    element: <Suspended><SettingsPage /></Suspended> },
                ],
              },

              // ── 팀장(safety_manager) 이상 — 대응 판단·제어·분석 ───────
              {
                element: <RequireRole min="safety_manager" />,
                children: [
                  { path: "dispersion",  element: <Suspended><DigitalTwinPage variant="simulator" /></Suspended> },
                  { path: "twin",        element: <Suspended><DigitalTwinPage /></Suspended> },
                  // 장치 제어 = 책임자 행위. 백엔드 command 는 이미 M+.
                  { path: "devices",     element: <Suspended><DevicesPage /></Suspended> },
                  { path: "overview",    element: <Suspended><DashboardPage /></Suspended> },
                  { path: "reports",     element: <Suspended><ReportsPage /></Suspended> },
                  // SOP 모의훈련 기록 = KPI 증빙, 팀장 접근 필요 (18 §3-1-4).
                  { path: "scenarios",   element: <Suspended><ScenariosPage /></Suspended> },
                ],
              },

              // ── 가람(org_admin) 이상 — 과제 증빙·자산 관리 ────────────
              {
                element: <RequireRole min="org_admin" />,
                children: [
                  // 협약 KPI 달성 현황 — 최종평가 관문. 고객 일상 업무 아님.
                  { path: "kpi",         element: <Suspended><KpiPage /></Suspended> },
                  // 임계값·도면·캡처 = 기술사 검토 대상 자산, 고객 편집 금지.
                  { path: "gas-db",      element: <Suspended><GasDbPage /></Suspended> },
                  { path: "blueprints",  element: <Suspended><BlueprintsPage /></Suspended> },
                  { path: "captures",    element: <Suspended><CapturesPage /></Suspended> },
                  { path: "admin/users", element: <Suspended><UsersPage /></Suspended> },
                ],
              },
              // ── 조직 관리 — 플랫폼 관리자 전용 ───────────────────────
              {
                element: <RequireRole min="super_admin" />,
                children: [
                  { path: "admin/orgs", element: <Suspended><OrgsPage /></Suspended> },
                ],
              },

              { path: "*",           element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
